/**
 * Magma Pawn Color Schemes
 *
 * Gradient-based color schemes for the magma pawn design.
 * The magma pawn is a chameleon/frog creature whose body color is player-specific.
 * Neutral elements (silver/grey head, white spot patterns, dark shadow areas) are
 * shared across all color variants.
 *
 * Color values are derived from the Figma SVG design (green player reference).
 */

export interface MagmaPawnColorScheme {
  /** Unique name used as gradient ID prefix to avoid SVG collisions */
  name: string;

  /** Body outer sphere gradient stops (paint1 - 3-stop horizontal gradient) */
  bodyOuterStart: string;
  bodyOuterMid: string;
  bodyOuterEnd: string;

  /** Body inner/bright sphere gradient stops (paint3 - 3-stop horizontal gradient) */
  bodyInnerStart: string;
  bodyInnerMid: string;
  bodyInnerEnd: string;

  /** Body front gradient stops (paint4 - 3-stop diagonal gradient) */
  bodyFrontStart: string;
  bodyFrontMid: string;
  bodyFrontEnd: string;

  /** Dark body shadow fill (fixed dark tone of player color) */
  bodyDarkShadow: string;

  /** Neck/torso gradient stops (paint5) */
  neckPrimary: string;
  neckSecondary: string;

  /** Main foliage/leaf accent color (bright) */
  accentBright: string;

  /** Dark foliage/leaf accent color */
  accentDark: string;

  /** Foliage detail colors */
  detailBright: string;
  detailMid: string;
  detailLight: string;

  /** Eye pupil fill color */
  eyePupil: string;
}

export const MAGMA_PAWN_COLOR_SCHEMES: Record<string, MagmaPawnColorScheme> = {
  blue: {
    name: 'blue',
    bodyOuterStart: '#0071BC',
    bodyOuterMid: '#1CA5E0',
    bodyOuterEnd: '#067ACD',
    bodyInnerStart: '#29ABE2',
    bodyInnerMid: '#00BFFF',
    bodyInnerEnd: '#0071BC',
    bodyFrontStart: '#006AAD',
    bodyFrontMid: '#29ABE2',
    bodyFrontEnd: '#0071BC',
    bodyDarkShadow: '#003F6B',
    neckPrimary: '#005899',
    neckSecondary: '#0095D1',
    accentBright: '#29ABE2',
    accentDark: '#006AAD',
    detailBright: '#00BFFF',
    detailMid: '#0095D1',
    detailLight: '#7AD4F5',
    eyePupil: '#29ABE2'
  },
  red: {
    name: 'red',
    bodyOuterStart: '#C1272D',
    bodyOuterMid: '#E53E30',
    bodyOuterEnd: '#8B0000',
    bodyInnerStart: '#F15A24',
    bodyInnerMid: '#FF3300',
    bodyInnerEnd: '#C1272D',
    bodyFrontStart: '#B51E24',
    bodyFrontMid: '#F15A24',
    bodyFrontEnd: '#C1272D',
    bodyDarkShadow: '#6B0000',
    neckPrimary: '#9E1E22',
    neckSecondary: '#D93528',
    accentBright: '#FF3300',
    accentDark: '#9E1E22',
    detailBright: '#FF5733',
    detailMid: '#D93528',
    detailLight: '#F4937B',
    eyePupil: '#F15A24'
  },
  yellow: {
    name: 'yellow',
    bodyOuterStart: '#F7931E',
    bodyOuterMid: '#FBB03B',
    bodyOuterEnd: '#CC7A00',
    bodyInnerStart: '#FCEE21',
    bodyInnerMid: '#FFFF00',
    bodyInnerEnd: '#F7931E',
    bodyFrontStart: '#E08000',
    bodyFrontMid: '#FCEE21',
    bodyFrontEnd: '#F7931E',
    bodyDarkShadow: '#7A4A00',
    neckPrimary: '#C47800',
    neckSecondary: '#F0A020',
    accentBright: '#FFFF00',
    accentDark: '#C47800',
    detailBright: '#FFEE44',
    detailMid: '#F0A020',
    detailLight: '#FADA82',
    eyePupil: '#FCEE21'
  },
  green: {
    name: 'green',
    bodyOuterStart: '#2EBC2A',
    bodyOuterMid: '#1CA500',
    bodyOuterEnd: '#067A00',
    bodyInnerStart: '#58E02B',
    bodyInnerMid: '#00FF00',
    bodyInnerEnd: '#35E02B',
    bodyFrontStart: '#00BA16',
    bodyFrontMid: '#00FF0C',
    bodyFrontEnd: '#067A00',
    bodyDarkShadow: '#124C17',
    neckPrimary: '#128406',
    neckSecondary: '#2BBF02',
    accentBright: '#4BFF00',
    accentDark: '#05A005',
    detailBright: '#71FF7B',
    detailMid: '#0BD10B',
    detailLight: '#73EA89',
    eyePupil: '#00FF00'
  }
};
